import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet'
import onHandProducts from '../data/onHandProducts';
import Lightbox from '../components/Lightbox';
import { imgSrc } from '../utils/img';
import { useCart } from '../contexts/CartContext';

const formatPrice = (cents, fallback) =>
  cents != null
    ? (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : fallback || '';

export default function OnHandDetail() {

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const item = useMemo(
    () => onHandProducts.find((p) => (p.slug || p.id) === slug),
    [slug]
  );

  if (!item) {
    return (
      <div className="px-4 py-10 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mt-4">Ready-to-Ship set not found.</h1>
      </div>
    );
  }

  const priceLabel = formatPrice(item.priceCents, item.price);
  const outOfStock =
    item.inventory?.trackType === 'stock' &&
    (item.inventory?.stockQty ?? 0) <= 0;

  const handleAdd = () => {
    if (outOfStock) return;
    addToCart({
      ...item,
      title: `${item.color} • ${item.style}`,
      price: priceLabel,
      quantity: 1,
    });
    navigate('/nails/cart');
  };

  return (
    <>
      <Helmet>
        <title>{`Ready To Ship — ${item.title} | NeNe Nail’d It`}</title>
        <meta name="description" content={`Ready-to-ship set — ${item.title}. Ships fast from Atlanta.`} />
        <link rel="canonical" href={`https://nenethearchitect.com/nails/onhand/${slug}`} />
        <meta property="og:title" content={`Ready To Ship — ${item.title}`} />
        <meta property="og:image" content={item.thumbnail} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="detail-bg min-h-screen py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-6 flex items-center justify-between gap-4">
            {item.inventory?.trackType === 'stock' && (
              <span
                className={`text-xs px-2 py-1 rounded ${outOfStock
                  ? 'bg-red-100 text-red-700'
                  : 'bg-emerald-100 text-emerald-700'
                  }`}
              >
                {outOfStock ? 'Sold out' : `Only ${item.inventory.stockQty} left`}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start bg-white rounded-2xl shadow p-6">
            {/* Image */}
            <div className="overflow-hidden rounded-xl">
              <img
                src={imgSrc(item.thumbnail)}
                alt={`${item.color} ${item.style}`}
                className="w-full h-[460px] object-cover"
                onClick={() => setLightboxOpen(true)}
                loading="lazy" decoding="async"
                onError={(e) => (e.currentTarget.src = '/nails-preview-placeholder.jpg')}
              />
            </div>
            {lightboxOpen && (
              <Lightbox
                src={imgSrc(item.thumbnail)}
                alt={`${item.color} ${item.style}`}
                onClose={() => setLightboxOpen(false)}
              />
            )}

            {/* Info */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  Ready to Ship
                </span>
                {item.isOneOfOne && (
                  <span className="text-xs uppercase tracking-wide bg-black text-white px-2 py-1 rounded">
                    One-of-One
                  </span>
                )}
              </div>

              <h1 className="text-3xl font-bold mt-3">{item.color}</h1>
              <p className="text-lg text-gray-700 mt-1">{item.style}</p>
              <p className="text-2xl font-semibold mt-2">{priceLabel}</p>

              <p className="text-sm text-gray-600 mt-4">
                What you see is exactly what you’ll get — this one’s ready to ship.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Shape</div>
                  <div className="font-medium capitalize">{item.shape}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Length</div>
                  <div className="font-medium capitalize">{item.length}</div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button
                  onClick={handleAdd}
                  disabled={outOfStock}
                  className={`btn-nene ${outOfStock ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {outOfStock ? 'Sold Out' : 'Add to Cart'}
                </button>
                <Link to="/nails" className="underline text-[#e86ba5] text-sm">
                  Continue browsing
                </Link>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                Ships for $10 via Stripe. Pickup available at pop-ups when marked in checkout.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
